import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Briefcase, FileText, TrendingUp, TrendingDown, DollarSign,
  Download, Share2, Calendar, PieChart, BarChart3, LineChart,
  Building2, Users, Cloud, Zap, CheckCircle, Clock, Sparkles,
  Mail, Presentation, FileBarChart, Bot, RefreshCw, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const AICFO = () => {
  const { shouldShowData } = useDemoData();
  const [generating, setGenerating] = useState(false);
  const [activeReport, setActiveReport] = useState('quarterly');
  const [reports, setReports] = useState(shouldShowData ? [
    { id: 1, name: 'Q4 2025 Cloud Spend Report', date: '2025-12-31', status: 'ready', type: 'quarterly' },
    { id: 2, name: 'November 2025 Executive Summary', date: '2025-11-30', status: 'ready', type: 'monthly' },
    { id: 3, name: 'Q3 2025 Board Presentation', date: '2025-09-30', status: 'ready', type: 'board' },
  ] : []);

  const metrics = shouldShowData ? {
    totalSpend: 847320,
    lastMonth: 892450,
    change: -5.1,
    forecast: 825000,
    savings: 127840,
    efficiency: 87
  } : {
    totalSpend: 0,
    lastMonth: 0,
    change: 0,
    forecast: 0,
    savings: 0,
    efficiency: 0
  };

  const generateReport = async (type) => {
    // Iter84ak — kick off real backend report generation; UI uses live
    // backend response. Falls back to local insertion on network error.
    setGenerating(true);
    toast.loading('AI is generating your report...', { id: 'generating' });
    let newReport = null;
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/finops/ai-cfo/report/${type}`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      newReport = r.data;
    } catch (_e) {
      newReport = {
        id: Date.now(),
        name: type === 'board' ? 'Q1 2026 Board Presentation' :
              type === 'quarterly' ? 'Q1 2026 Cloud Spend Report' :
              'January 2026 Executive Summary',
        date: new Date().toISOString().split('T')[0],
        status: 'ready',
        type,
      };
    }
    await new Promise(resolve => setTimeout(resolve, 1800));
    setReports(prev => [newReport, ...prev]);
    setGenerating(false);
    toast.success('Report generated successfully!', { id: 'generating' });
  };

  const reportTemplates = [
    {
      type: 'board',
      title: 'Board Presentation',
      desc: 'Executive summary for board meetings',
      icon: Presentation,
      color: 'violet',
      slides: ['Executive Summary', 'Cost Trends', 'Savings Achieved', 'Forecast', 'Recommendations']
    },
    {
      type: 'quarterly',
      title: 'Quarterly Report',
      desc: 'Detailed quarterly analysis',
      icon: FileBarChart,
      color: 'blue',
      slides: ['Overview', 'By Service', 'By Team', 'Anomalies', 'Optimization', 'Next Quarter']
    },
    {
      type: 'monthly',
      title: 'Monthly Summary',
      desc: 'Quick monthly executive brief',
      icon: FileText,
      color: 'emerald',
      slides: ['Highlights', 'Spend vs Budget', 'Top Changes', 'Action Items']
    }
  ];

  return (
    <div className="space-y-6" data-testid="ai-cfo-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Briefcase className="h-7 w-7 text-emerald-500" />
            AI CFO
          </h1>
          <p className="text-slate-400 dark:text-gray-400 mt-1">
            Auto-generate board presentations and executive reports in seconds
          </p>
        </div>
        <Badge className="bg-emerald-100 text-emerald-700">
          <Sparkles className="w-3 h-3 mr-1" />
          AI-Powered
        </Badge>
      </div>

      {!shouldShowData && (
        <EmptyDataState title="AI CFO" description="Connect your cloud providers to generate AI-powered executive reports and board presentations from your real cost data." />
      )}

      {shouldShowData && (
      <>
      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Current Month</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  ${(metrics.totalSpend / 1000).toFixed(0)}K
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-slate-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">vs Last Month</p>
                <p className={`text-2xl font-bold ${metrics.change < 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {metrics.change > 0 ? '+' : ''}{metrics.change}%
                </p>
              </div>
              {metrics.change < 0 ? 
                <TrendingDown className="w-8 h-8 text-emerald-300" /> :
                <TrendingUp className="w-8 h-8 text-red-300" />
              }
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">YTD Savings</p>
                <p className="text-2xl font-bold text-emerald-600">
                  ${(metrics.savings / 1000).toFixed(0)}K
                </p>
              </div>
              <Zap className="w-8 h-8 text-emerald-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Efficiency Score</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {metrics.efficiency}%
                </p>
              </div>
              <BarChart3 className="w-8 h-8 text-blue-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Report Generation */}
      <Card>
        <CardHeader>
          <CardTitle>Generate New Report</CardTitle>
          <CardDescription>AI will analyze your data and create a professional presentation</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            {reportTemplates.map((template) => (
              <div 
                key={template.type}
                className={`p-5 rounded-xl border-2 border-dashed hover:border-solid transition-all cursor-pointer ${
                  template.color === 'violet' ? 'border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-cyan-900/20' :
                  template.color === 'blue' ? 'border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-blue-900/20' :
                  'border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
                }`}
                onClick={() => !generating && generateReport(template.type)}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
                  template.color === 'violet' ? 'bg-emerald-100 text-emerald-600' :
                  template.color === 'blue' ? 'bg-emerald-100 text-emerald-600' :
                  'bg-emerald-100 text-emerald-600'
                }`}>
                  <template.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{template.title}</h3>
                <p className="text-sm text-slate-400 mb-3">{template.desc}</p>
                <div className="flex flex-wrap gap-1">
                  {template.slides.map((slide, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {slide}
                    </Badge>
                  ))}
                </div>
                <Button 
                  className={`w-full mt-4 ${
                    template.color === 'violet' ? 'bg-cyan-600 hover:bg-cyan-700' :
                    template.color === 'blue' ? 'bg-blue-600 hover:bg-blue-700' :
                    'bg-emerald-600 hover:bg-emerald-700'
                  } text-white`}
                  disabled={generating}
                >
                  {generating ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Generate
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Sample Board Presentation Preview */}
      <Card className="overflow-hidden">
        <CardHeader className="bg-emerald-600 bg-gradient-to-r from-emerald-600 to-cyan-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white">Q1 2026 Board Presentation Preview</CardTitle>
              <CardDescription className="text-cyan-200">AI-generated executive summary</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" className="bg-white/20 hover:bg-white/30 text-white">
                <Share2 className="w-4 h-4 mr-1" />
                Share
              </Button>
              <Button size="sm" variant="secondary" className="bg-white/20 hover:bg-white/30 text-white">
                <Download className="w-4 h-4 mr-1" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="grid md:grid-cols-2 divide-x divide-slate-200 dark:divide-slate-700">
            {/* Slide 1: Executive Summary */}
            <div className="p-6">
              <Badge className="mb-3 bg-emerald-100 text-emerald-700">Slide 1</Badge>
              <h3 className="text-lg font-bold mb-4">Executive Summary</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Cloud spend reduced by <strong>5.1%</strong> vs last quarter</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Achieved <strong>$127K</strong> in cost savings YTD</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Efficiency score improved to <strong>87%</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-500" />
                  <span>3 optimization opportunities pending review</span>
                </div>
              </div>
            </div>
            
            {/* Slide 2: Cost Breakdown */}
            <div className="p-6">
              <Badge className="mb-3 bg-emerald-100 text-emerald-700">Slide 2</Badge>
              <h3 className="text-lg font-bold mb-4">Cost by Service</h3>
              <div className="space-y-3">
                {[
                  { name: 'Compute (EC2/GCE)', pct: 45, color: 'bg-orange-500' },
                  { name: 'Database (RDS/Snowflake)', pct: 28, color: 'bg-emerald-500' },
                  { name: 'Storage (S3/GCS)', pct: 15, color: 'bg-emerald-500' },
                  { name: 'Other Services', pct: 12, color: 'bg-slate-400' }
                ].map((item, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between text-sm mb-1">
                      <span>{item.name}</span>
                      <span className="font-medium">{item.pct}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div className={`h-full ${item.color} rounded-full`} style={{ width: `${item.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Previous Reports */}
      <Card>
        <CardHeader>
          <CardTitle>Previous Reports</CardTitle>
          <CardDescription>Access and share previously generated reports</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {reports.map((report) => (
              <div key={report.id} className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    report.type === 'board' ? 'bg-emerald-100 text-emerald-600' :
                    report.type === 'quarterly' ? 'bg-emerald-100 text-emerald-600' :
                    'bg-emerald-100 text-emerald-600'
                  }`}>
                    {report.type === 'board' ? <Presentation className="w-5 h-5" /> :
                     report.type === 'quarterly' ? <FileBarChart className="w-5 h-5" /> :
                     <FileText className="w-5 h-5" />}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">{report.name}</p>
                    <p className="text-sm text-slate-400">Generated {report.date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-100 text-emerald-700">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Ready
                  </Badge>
                  <Button size="sm" variant="outline">
                    <Download className="w-4 h-4 mr-1" />
                    Download
                  </Button>
                  <Button size="sm" variant="outline">
                    <Mail className="w-4 h-4 mr-1" />
                    Email
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
};

export default AICFO;
